import type { PropTypes } from './PrizePoolBox.types';
import type { ReactElement } from 'react';

import { Card, Flex, Heading, Skeleton, Text } from '@rango-dev/whale';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { displayUsdValue } from '../../Profile/ProfileSummary/ProfileSummary.helpers';
import { ProgressBar } from '../ProgressBar';

import styles from './PrizePoolBox.module.css';

export function PrizePoolBox(props: PropTypes): ReactElement {
  const { t } = useTranslation();
  const { className, logo, prizePoolIcons, title, description, type, detailsData, detailsLoading } =
    props;

  const currentPrizePool =
    detailsData?.prizePool &&
    detailsData.prizePool.find(
      (item) =>
        detailsData.doneCriteriaAmount > item.start && detailsData.doneCriteriaAmount <= item.end,
    );

  if (detailsLoading || !detailsData) {
    return (
      <Skeleton
        className={styles.skeleton}
        height={{ initial: '396px', sm: '388px', md: '240px' }}
      />
    );
  }

  const targetPrize = detailsData?.prizePool[detailsData?.prizePool.length - 1]?.prize || 0;

  return (
    <Card size={{ initial: '2', md: '3' }} variant="surface" className={className}>
      <Flex
        width="100%"
        height="100%"
        direction={{ initial: 'column', md: 'row' }}
        align="center"
        gap="5">
        <Flex
          width="100%"
          minWidth="0"
          align={{ initial: 'center', xl: 'start' }}
          gap="4"
          flexGrow={{ md: '1' }}
          flexShrink={{ md: '0' }}
          flexBasis={{ md: '0' }}>
          <img src={logo} alt={title} className={styles.cupImage} />
          <Flex
            minWidth="0"
            flexGrow="1"
            flexBasis="0"
            direction="column"
            justify="center"
            gap="5"
            className={styles.leftContent}>
            <Flex direction="column" width="100%" gap="1">
              <Heading as="h2" size="6" weight="bold" className={styles.sloganTitle}>
                {title}
              </Heading>
              {description && (
                <Text as="p" size="2" weight="medium">
                  {description}
                </Text>
              )}
            </Flex>

            <Flex direction="column" width="100%" gap="1">
              <Text as="p" size="2" weight="medium" className={styles.rewardsPoolTitle}>
                {t('Current Rewards Pool')}
              </Text>
              <Text as="p" size="6" weight="bold" className={styles.prizeValue} wrap={'nowrap'}>
                <span className={styles.doneCriteriaAmount}>
                  {currentPrizePool?.prize ? displayUsdValue(currentPrizePool?.prize) : '$0'}
                </span>
                <span>/{displayUsdValue(targetPrize)}</span>
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {detailsData?.prizePool && (
          <Flex
            direction="column"
            width={{ initial: '100%', md: 'auto' }}
            align={{ md: 'end' }}
            gap="2">
            {detailsData.prizePool.map((item, index) => (
              <ProgressBar
                doneCriteriaAmount={detailsData?.doneCriteriaAmount || 0}
                key={item.start}
                icon={prizePoolIcons[index % prizePoolIcons.length]}
                prize={item.prize}
                start={item.start}
                end={item.end}
                type={type}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
